import { z } from "zod";
import { PermissionSchema } from "./permissions";
import { UserStatusReportSelectionSchema } from "./statusReports";

export enum DashboardInviteStatus {
  NOT_INVITED = "NOT_INVITED",
  INVITED = "INVITED",
}

/** User info visible to all authenticated users (not just admins). */
export const PublicUserSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string(),
  profilePictureUrl: z.string().nullable().optional(),
});

export type PublicUser = z.infer<typeof PublicUserSchema>;

/** The authenticated user's own record as returned by GET /users/me. */
export const AuthenticatedUserSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string(),
  role: z.string(),
  roleId: z.string().nullable(),
  dashboardInviteStatus: z.enum(DashboardInviteStatus),
  statusReportTemplate: UserStatusReportSelectionSchema,
  quoteViewNotificationsEnabled: z.boolean(),
  profilePictureUrl: z.string().nullable().optional(),
  organization: z.object({
    id: z.string(),
    name: z.string(),
  }),
});

export type AuthenticatedUser = z.infer<typeof AuthenticatedUserSchema>;

/**
 * Request body for PUT /users/me: the caller updating their own record —
 * name, profile picture, and self-scoped preferences. Deliberately separate
 * from the admin user-update payload: nothing here grants permissions or
 * changes identity. `profilePictureAssetId` references an uploaded asset;
 * null removes the picture.
 */
export const UpdateMyProfileBodySchema = z.object({
  firstName: z.string().trim().min(1).optional(),
  lastName: z.string().trim().optional(),
  profilePictureAssetId: z.string().nullable().optional(),
  statusReportTemplate: UserStatusReportSelectionSchema.optional(),
  quoteViewNotificationsEnabled: z.boolean().optional(),
});

/** Request body for updating the caller's own profile. */
export type UpdateMyProfileBody = z.infer<typeof UpdateMyProfileBodySchema>;

/**
 * Response payload for GET /users/me: the authenticated user plus the raw
 * permission strings (e.g. "settings.edit") their role grants.
 */
export const MeResponseSchema = z.object({
  user: AuthenticatedUserSchema,
  permissions: z.array(PermissionSchema),
});

export type MeResponse = z.infer<typeof MeResponseSchema>;

/** Response payload for GET /users/members: the org's active members. */
export const MembersResponseSchema = z.object({
  users: z.array(PublicUserSchema),
});

/** Response payload for GET /users/members. */
export type MembersResponse = z.infer<typeof MembersResponseSchema>;
